import apiClient from '../lib/apiClient';
import { AxiosResponse } from 'axios';
import { User } from '../store/userStore';

// --- 타입 정의 (모든 모델에 priority 추가) ---
interface HateoasResponse<T> {
    _embedded: {
        getList: T[];
    };
    // 페이지네이션 정보가 필요하다면 여기에 page 타입 정의를 추가할 수 있습니다.
    page?: {
        size: number;
        totalElements: number;
        totalPages: number;
        number: number;
    }
}
export interface Board {
    id: string;
    name: string;
    createDateTime: string;
    priority: number;
}
export interface CardList {
    id: string;
    name: string;
    boardId: string;
    priority: number;
}
export interface Label {
    name: string;
    color: string;
}
export interface ReportLevel {
    name: string;
    priority: number;
}
export interface Card {
    id: string;
    title: string;
    description: string | null;
    cardListId: string;
    boardId: string;
    createDateTime: string;
    modifyDateTime: string;
    priority: number;
    attachments: Attachment[] | null;
    labels: Label[] | null;
    reportLevels: ReportLevel[] | null;
    startDate: string | null;
    dueDate: string | null;
    completed: boolean | null;
    status: string | null;
    userId?: string;
    // ✨ [추가] 실제 업무 관련 시간 필드
    inputTime?: number;      // 업무 진행 시간 (밀리초)
    realStartTime?: string;  // 실제 업무 시작 시각
    realEndTime?: string;    // 실제 업무 종료 시각
    state?: string;
    predecessorIds?: string[];
    projectId: string;
    taskCode?: string;
    // ✨ [추가] 긴급 업무 복귀를 위한 필드
    prevEmergencyId?: string;
    isEmergency?: boolean; // ✨ [추가] 긴급 업무 여부 구분 필드
    // 담당자. 서버는 정해 두지 않았으면 만든 사람(userId)을 준다.
    assigneeId?: string | null;
}

export interface ProjectCardNodeData extends Card {
    projectMembers: string[]; // 프로젝트의 현재 멤버 ID 목록
    projectOwner: string;      // 프로젝트 소유자 ID
}

export interface Attachment {
    attachmentId: string;
    originalFilename: string;
    storedFilename: string;
    fileSize: number;
    contentType: string;
    uploadTimestamp: string;
}

export interface Comment {
    id: string;
    content: string;
    cardId: string;
    userId: string;
    createDateTime: string;
    modifyDateTime: string;
}

interface LoginParams { id: string; password: string; }
interface RegisterParams { id: string; password: string; name: string; }

interface CardSearchParams {
    title?: string;
    description?: string;
    completed?: boolean;
    status?: string;
    // 추가된 필드들
    cardListId?: string;
    boardId?: string;
    labels?: Label[]; // 백엔드는 List<Label> 형태로 받음
    userId?: string;
    // 날짜 범위 등 추가 가능
    startDate?: string;
    modifyDateTime?: string;
}

// --- API 함수들 ---

// 인증
export const checkAuthStatus = (): Promise<AxiosResponse<User>> => {
    return apiClient.get('/auth');
};
export const logout = (): Promise<AxiosResponse> => {
    return apiClient.post('/auth/logout');
};
export const login = (params: LoginParams): Promise<AxiosResponse<{ access_token: string }>> => {
    const searchParams = new URLSearchParams();
    searchParams.append('username', params.id);
    searchParams.append('password', params.password);
    return apiClient.post('/auth/login', searchParams, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
};
export const register = (params: RegisterParams): Promise<AxiosResponse> => {
    return apiClient.post('/auth', params);
};


// 보드
export const getBoardById = (boardId: string): Promise<AxiosResponse<Board>> => {
    return apiClient.get(`/board/${boardId}`);
};
export const getBoardList = (): Promise<AxiosResponse<HateoasResponse<Board>>> => {
    return apiClient.get('/board/list?sort=priority,asc');
};
export const createBoard = (params: { name: string; priority: number }): Promise<AxiosResponse> => {
    return apiClient.post('/board', params);
};
export const updateBoard = (params: Partial<Board> & { id: string }): Promise<AxiosResponse> => {
    return apiClient.put('/board', params);
};
export const deleteBoard = (boardId: string): Promise<AxiosResponse> => {
    return apiClient.delete('/board', { data: { ids: [boardId] } });
};


// 리스트
export const getListsByBoardId = (boardId: string): Promise<AxiosResponse<HateoasResponse<CardList>>> => {
    return apiClient.get(`/card-list/list?boardId=${boardId}&sort=priority,asc`);
};
export const createList = (params: { name: string; boardId: string; priority: number }): Promise<AxiosResponse> => {
    return apiClient.post('/card-list', params);
};
export const updateList = (params: Partial<CardList> & { id: string }): Promise<AxiosResponse> => {
    return apiClient.put('/card-list', params);
};

export const deleteList = (listId: string): Promise<AxiosResponse> => {
    return apiClient.delete('/card-list', { data: { ids: [listId] } });
};


// 카드
export const getCardsByStatus = (searchParams: CardSearchParams = {}): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    // URL 쿼리 파라미터를 동적으로 구성합니다.
    const params = new URLSearchParams({});
    params.append('status', searchParams.status || '');

    // Body는 비워두고, 생성된 쿼리 파라미터를 URL에 붙여 POST 요청을 보냅니다.
    return apiClient.post(`/card/list?${params.toString()}`, {});
};

export const getCardsByListId = (boardId: string, cardListId: string, searchParams: CardSearchParams = {}): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    // URL 쿼리 파라미터를 동적으로 구성합니다.
    const params = new URLSearchParams({
        boardId,
        cardListId,
        sort: 'priority,asc',
    });

    // 검색어가 있을 경우, title과 description 파라미터를 추가합니다.
    if (searchParams.title && searchParams.title.trim() !== '') {
        params.append('title', searchParams.title);
    }
    if (searchParams.description && searchParams.description.trim() !== '') {
        params.append('description', searchParams.description);
    }
    if (searchParams.completed !== undefined) {
        params.append('completed', String(searchParams.completed));
    }

    // Body는 비워두고, 생성된 쿼리 파라미터를 URL에 붙여 POST 요청을 보냅니다.
    return apiClient.post(`/card/list?${params.toString()}`, {});
};
// 특정 보드에 속한 모든 카드를 조건으로 조회합니다. (listId 없이)
export const getCardsByBoard = (boardId: string, searchParams: CardSearchParams = {}): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    const params = new URLSearchParams({
        boardId,
        sort: 'modifyDateTime,desc', // 완료된 순서대로 최신순 정렬
    });

    // Body가 아닌 URL 쿼리 파라미터로 'completed' 조건을 전달합니다.
    if (searchParams.completed === true) {
        params.append('completed', 'true');
    }

    // Body로 검색 조건을 전달합니다.
    return apiClient.post(`/card/list?${params.toString()}`, searchParams);
};

// ✨ [수정] labels는 Body로, 나머지는 Query Parameter로 분리하여 전송
export const getAllCardsForUser = (
    page: number = 0,
    size: number = 20,
    sort: string | string[] = 'createDateTime,desc',
    searchParams: CardSearchParams = {}
): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
    });

    // 정렬 파라미터 처리
    if (Array.isArray(sort)) {
        if (sort.length > 0) {
            sort.forEach(s => params.append('sort', s));
        } else {
            params.append('sort', 'createDateTime,desc');
        }
    } else {
        params.append('sort', sort);
    }

    const bodyData: { labels?: Label[] } = {};

    // searchParams 분리: labels만 Body로, 나머지는 Query Param으로
    Object.keys(searchParams).forEach(key => {
        const value = (searchParams as any)[key];
        if (value === undefined || value === null || value === '') return;

        if (key === 'labels') {
            // labels는 Body에 담기
            bodyData.labels = value as Label[];
        } else {
            // labels 외의 모든 필드는 Query Parameter에 담기
            params.append(key, String(value));
        }
    });

    return apiClient.post(`/card/list?${params.toString()}`, bodyData);
};

export const getCardById = (cardId: string): Promise<AxiosResponse<Card>> => {
    return apiClient.get(`/card/${cardId}`);
};

// ✨ [추가] 기간별 카드 조회 API
// FullCalendar가 주는 날짜 범위(start, end)를 쿼리 파라미터로 전송합니다.
export const getCardsForCalendar = (from: string, to: string): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    const params = new URLSearchParams({
        sort: 'startDate,asc',
        startDate: from,
        dueDate: to
    });

    // 기존처럼 POST /card/list?params... 형태로 호출
    return apiClient.post(`/card/list?${params.toString()}`, {});
};

export const createCard = (params: Partial<Omit<Card, 'id'>>): Promise<AxiosResponse<Card>> => {
    return apiClient.post('/card', params);
};
export const updateCard = (params: Partial<Card> & { id: string }): Promise<AxiosResponse> => {
    return apiClient.put('/card', params);
};
export const deleteCard = (cardId: string): Promise<AxiosResponse> => {
    return apiClient.delete('/card', { data: { ids: [cardId] } });
};

// --- 댓글 API ---
export const getCommentList = (cardId: string): Promise<AxiosResponse<HateoasResponse<Comment>>> => {
    // --- THIS IS THE FIX ---
    // 정렬 순서를 'asc' (오름차순) 에서 'desc' (내림차순)으로 변경합니다.
    return apiClient.get(`/comment/list?cardId=${cardId}&sort=createDateTime,desc`);
};
export const createComment = (params: { content: string; cardId: string }): Promise<AxiosResponse<Comment>> => {
    return apiClient.post('/comment', params);
};
export const updateComment = (params: { id: string; content: string }): Promise<AxiosResponse<Comment>> => {
    return apiClient.put('/comment', params);
};
export const deleteComment = (commentId: string): Promise<AxiosResponse> => {
    return apiClient.delete('/comment', { data: { ids: [commentId] } });
};

// --- 첨부파일 API ---
export const uploadAttachment = (cardId: string, file: File): Promise<AxiosResponse<Attachment>> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/card/${cardId}/attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const downloadAttachment = async (cardId: string, attachmentId: string, filename: string) => {
    // API 명세에 맞는 올바른 URL을 생성합니다.
    const response = await apiClient.get(`/card/${cardId}/attachment/${attachmentId}`, {
        responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const deleteAttachment = (cardId: string, attachmentId: string): Promise<AxiosResponse> => {
    // API 명세에 맞는 올바른 URL로 DELETE 요청을 보냅니다.
    return apiClient.delete(`/card/${cardId}/attachment/${attachmentId}`);
};


// --- 감사 로그(Activity Log) 관련 타입 ---
export type ActivityType = 'CREATE' | 'UPDATE' | 'DELETE';

export interface DiffValue {
    from: any;
    to: any;
}

export type TargetType = 'CARD' | 'COMMENT';

export interface ActivityLog {
    id: string; // MongoDB ObjectId
    userId: string;
    activity: string; // "업무 상태 변경" 등
    type: ActivityType;
    targetId: string;
    diff: Record<string, DiffValue> | null; // Map<String, Object> 대응
    timestamp: string; // LocalDateTime string
    targetType: TargetType;
    rootId: string;
    description: string;
}

// 로그 조회 API
export const getActivityLogs = (date: string, targetId?: string): Promise<AxiosResponse<ActivityLog[]>> => {
    let url = `/log?date=${date}`;
    if (targetId && targetId !== 'all') {
        url += `&targetId=${targetId}`;
    }
    return apiClient.get(url);
};


// // --- [API] ---
// export interface ReportResponse {
//     id: string;
//     userId: string;
//     report: string;
//     startDate: string;
//     endDate: string;
//     createDateTime: string;
// }
//
// // [신규] 리포트 조회 (GET)
// export const fetchReport = async (startDate: string, endDate: string): Promise<ReportResponse | null> => {
//     try {
//         const response = await apiClient.get(`/report`, {
//             params: { startDate, endDate }
//         });
//         return response.data;
//     } catch (error) {
//         // 404 Not Found 등 에러 발생 시 null 반환 (리포트 없음 처리)
//         return null;
//     }
// };
//
// // 리포트 생성 (POST)
// export const createReport = async (startDate: string, endDate: string): Promise<ReportResponse> => {
//     const response = await apiClient.post(`/report`, null, {
//         params: { startDate, endDate }
//     });
//     return response.data;
// };

// --- [API Types] ---
export interface ReportData {
    id: string;
    userId: string;
    report: string;
    startDate: string;
    endDate: string;
    createDateTime: string;
}

export interface ReportListResponse {
    content: ReportData[];
    pageable: any;
    totalElements: number;
    totalPages: number;
    last: boolean;
}

// --- [API Functions] ---
export const fetchReportList = async (page: number = 0, size: number = 10): Promise<ReportListResponse> => {
    // 제공해주신 스펙: /report/list?page=0&size=10
    const response = await apiClient.get(`/report/list`, {
        params: { page, size, sort: 'createDateTime,desc' }
    });
    return response.data;
};

export const createReport = async (startDate: string, endDate: string): Promise<ReportData> => {
    const response = await apiClient.post(`/report`, null, {
        params: { startDate, endDate }
    });
    return response.data;
};

export type ProjectStatus = 'ACTIVE' | 'COMPLETED';

export interface Project {
    id: string;
    title: string;
    description: string;
    ownerId: string;
    memberIds: string[];
    editorIds: string[];
    status?: ProjectStatus;
    createDateTime: string;
    modifyDateTime: string;
}

// 프로젝트 생성
export const createProject = (params: { title: string; description?: string; memberIds?: string[] }): Promise<AxiosResponse<Project>> => {
    return apiClient.post('/project', params);
};

// 내 프로젝트 목록
export const getProjectList = (): Promise<AxiosResponse<Project[]>> => {
    return apiClient.get('/project/list');
};

// 프로젝트 상세
export const getProjectById = (id: string): Promise<AxiosResponse<Project>> => {
    return apiClient.get(`/project/${id}`);
};

// 프로젝트 수정
export const updateProject = (params: Partial<Project> & { id: string }): Promise<AxiosResponse<Project>> => {
    return apiClient.put('/project', params);
};

// 프로젝트 삭제
export const deleteProject = (id: string): Promise<AxiosResponse> => {
    return apiClient.delete(`/project/${id}`);
};

// 프로젝트에 속한 카드 목록 (다이어그램용)
export const getProjectCards = (projectId: string): Promise<AxiosResponse<Card[]>> => {
    return apiClient.get(`/project/${projectId}/cards`);
};

// 카드들을 프로젝트에 할당
export const assignCardsToProject = (projectId: string, cardIds: string[]): Promise<AxiosResponse> => {
    return apiClient.post(`/project/${projectId}/assign-cards`, cardIds);
};

// (추가) 프로젝트 할당을 위해 '프로젝트가 없는' 내 카드 조회
export const getMyIdleCards = (): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    // 백엔드에서 projectId가 null인 것을 찾는 쿼리가 필요.
    // 임시로 기존 조회 API 활용 (필터링은 프론트 혹은 백엔드 스펙 협의)
    // 여기서는 기존 getAllCardsForUser를 쓰고 프론트에서 거르는 방식으로 일단 진행 (데이터 많으면 API 따로 파야 함)
    return getAllCardsForUser(0, 1000, 'createDateTime,desc', {});
};

export const unassignCardFromProject = (cardId: string): Promise<AxiosResponse> => {
    return apiClient.post(`/card/${cardId}/unassign-project`);
};

// 사용자 검색 API 함수
export const searchUsers = (keyword: string) => {
    // 응답 데이터가 WorkUser 인터페이스의 배열임을 명시합니다.
    return apiClient.get<User[]>(`/users/search`, {
        params: { keyword }
    });
};

export const updateCardEdges = (params: Partial<Card> & { id: string }): Promise<AxiosResponse> => {
    return apiClient.patch(`/card/connect-edges`, params);
};

/**
 * 완료된 카드 목록 조회 (POST 방식)
 * 완료된 카드가 있는지 확인하기 위한 용도
 */
export const getCompletedCards = (page = 0, size = 10): Promise<AxiosResponse<HateoasResponse<Card>>> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    params.append('status', 'IN_PROGRESS'); // 완료된 것만 필터링

    // 유저님이 요청하신 주소와 메서드 방식 반영
    return apiClient.post(`/card/list?${params.toString()}`, {});
};